/**
 * Surface styling for the onboarding assistant — single source of truth for
 * the /start page's look. The interview logic in OnboardingAssistant.tsx never
 * changes when the design language does; recolor or reshape here.
 *
 * Language: cool paper with frosted white panels, hairline borders, ink
 * text, violet accents — the editorial system of the rest of the site.
 */
import { alpha } from "@mui/material/styles";
import { colors } from "@/theme/colors";
import { fonts, navLabelSx } from "@/theme/tokens";

export const INK = colors.background; // page background (cool paper)
export const TXT = colors.textPrimary; // ink text
export const TXT_DIM = colors.textSecondary; // muted taupe text
export const GLASS = alpha(colors.paper, 0.7); // frosted white panels
export const GLASS_STRONG = colors.tintDeep; // violet-tint fill (idle send)
export const BORDER = colors.divider; // hairline borders

/** The site's mono label, tuned slightly smaller/wider for the chat chrome. */
export const monoLabel = {
  ...navLabelSx,
  letterSpacing: "0.16em",
  fontSize: "0.66rem",
} as const;

/** Quiet mono text action (back, start over, talk to a person). */
export const quietActionSx = {
  ...monoLabel,
  px: 0,
  color: TXT_DIM,
  "&:hover": { color: TXT },
} as const;

/** Frosted text field used by the input bar and email fallback. */
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: TXT,
    backgroundColor: GLASS,
    backdropFilter: "blur(12px)",
    borderRadius: "4px",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: colors.iconHover },
    "&.Mui-focused fieldset": { borderColor: colors.primary },
  },
  "& .MuiInputLabel-root": { color: TXT_DIM },
  "& .MuiInputLabel-root.Mui-focused": { color: colors.primary },
} as const;

export const chipSx = {
  color: TXT,
  borderColor: BORDER,
  backgroundColor: GLASS,
  backdropFilter: "blur(8px)",
  fontSize: "0.85rem",
  "&:hover": {
    borderColor: colors.primary,
    backgroundColor: colors.tintDeep,
  },
} as const;

/** Entrance animation for a chat row (references the global `msgIn` keyframes). */
export const entranceSx = {
  animation: "msgIn 0.4s cubic-bezier(0.21,0.47,0.32,0.98) both",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

export const markdownSx = {
  "& > :first-of-type": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& p": { m: 0, mb: 1 },
  "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
  "& li": { mb: 0.25 },
  "& a": { color: colors.primary },
  "& strong": { fontWeight: 700 },
  "& code": {
    fontFamily: fonts.mono,
    fontSize: "0.9em",
    backgroundColor: alpha(colors.primary, 0.1),
    px: 0.5,
    borderRadius: "4px",
  },
} as const;
